import { env } from '@/lib/env';

/**
 * Non-component helpers for the seamless re-auth modal (TRU-301). Kept out of
 * `ReAuthModal.tsx` so that file exports only components (Vite Fast Refresh
 * rule). Covers the OAuth path, which is a full-page redirect to the backend
 * and back via `/auth/oauth-callback` — the route + workspace must survive that
 * round-trip in localStorage since in-memory state is destroyed.
 */

const API_BASE = env.apiUrl;

const RETURN_TO_KEY = 'reauth_return_to';
const WORKSPACE_ID_KEY = 'reauth_workspace_id';

/**
 * Before redirecting to an OAuth provider, stash where to come back to. The
 * workspace also travels in the OAuth `state` (via the `workspaceId` query
 * param on the URL below) so the token is minted for the right workspace; the
 * stored copy is only used to restore the route on return.
 */
export function persistReturnRoute(workspaceId?: number): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(RETURN_TO_KEY, window.location.pathname + window.location.search);
  if (workspaceId) localStorage.setItem(WORKSPACE_ID_KEY, String(workspaceId));
}

/**
 * Reads and clears the persisted return route. Returns null for the normal
 * first-login OAuth flow (no re-auth in progress), so the callback falls back
 * to its default landing route.
 */
export function consumeReturnRoute(): string | null {
  if (typeof window === 'undefined') return null;
  const to = localStorage.getItem(RETURN_TO_KEY);
  localStorage.removeItem(RETURN_TO_KEY);
  localStorage.removeItem(WORKSPACE_ID_KEY);
  return to;
}

/** Builds the provider OAuth start URL, carrying the workspace for exact restore. */
export function buildOAuthUrl(provider: 'google' | 'microsoft', workspaceId?: number): string {
  const base = `${API_BASE}/auth/${provider}`;
  return workspaceId ? `${base}?workspaceId=${workspaceId}` : base;
}
